import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { facilityReducer } from "../../../state/reducers";
import { IFacility } from "../../../utils/types/models";

const FacilityEditor: React.FC<IAddEntityScreenProps<IFacility> | IEditEntityScreenProps<IFacility>> = (props: IAddEntityScreenProps<IFacility> | IEditEntityScreenProps<IFacility>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(facilityReducer, getInitialState<IFacility>());
	const initialFacility: IFacility = {
		id: (props as IEditEntityScreenProps<IFacility>).recordId || 0,
		name: "",
		active: true
	};
	const [facility, setFacility] = useState<IFacility>(initialFacility);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setFacility(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setFacility(handleChange(facility, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(facility.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IFacility): void {
		if (record && !close) {
			setFacility(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IFacility>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={facility}
			endpoint={Endpoint.Facilities}
			entityState={state}
			entityType={t("facility")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={facility.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={facility.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={facility.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default FacilityEditor;
